@let hasSecondaryInsurance = hasSecondaryInsurance$ | async;
@let hasSecondary = inputs.hasSecondary;
@let secondaryInsurance = secondaryInsurance$ | async;
@let estimate = estimate$ | async;
@let primaryInsurance = primaryInsurance$ | async;

<mat-card class="card-table has-progress-bar" data-balance-benefits-panel>
  <mat-progress-bar [mode]="(benefitsLoading$ | async) ? 'indeterminate' : 'determinate'"></mat-progress-bar>
  <mat-card-title fxLayout="row" fxLayoutAlign="space-between center">
    <span>{{ 'estimate.benefits' | translate }}</span>

    <button mat-icon-button
      [satPopoverAnchor]="secondaryInsuranceOptions"
      (click)="secondaryInsuranceOptions.open()">
      <mat-icon>more_vert</mat-icon>
    </button>
  </mat-card-title>
  <mat-card-content>
    @if (!shopper && estimate?.status === draftStatus && primaryInsurance === null) {
      <p class="estimate-benefits-notice text-sm text-warn" data-estimate-benefits-self-pay-notice>
        {{ 'estimate.benefits-self-pay' | translate }}
      </p>
    }
    @if (!shopper && estimate?.status === draftStatus && primaryInsurance?.status === PatientInsuranceStatus.NotVerified) {
      <p class="estimate-benefits-notice text-sm text-warn" data-estimate-benefits-unverified-notice>
        {{ 'estimate.benefits-unverified' | translate }}
      </p>
    }
    <div>
      <table
        class="mat-mdc-table mat-bordered-table estimate-table charges"
        cellspacing="0"
        cellpadding="0"
      >
        <thead>
        <tr class="mat-mdc-row mat-header-row emptyheading">
          <th class="mat-mdc-cell mat-mdc-header-cell input">
                <span class="text-primary uppercase text-sm">
                  {{ 'patient-details.estimate-card.table-two.benefit-item' | translate }}
                </span>
          </th>
          <th class="mat-mdc-cell mat-mdc-header-cell text-center output">
                <span class="text-primary uppercase text-sm">
                  {{ 'patient-details.estimate-card.table-two.benefit-amount' | translate }}
                </span>
          </th>
        </tr>
        </thead>
        <tbody>
        @if (primaryInsurances$ | async; as insurances) {
          @if (insurances?.length > 1) {
            <tr class="mat-mdc-row">
              <td class="mat-mdc-cell input">
                {{ 'common.primary-insurance' | translate }}
              </td>
              <td class="mat-mdc-cell editablefield text-center output">
                <mat-form-field class="w-100" subscriptSizing="dynamic">
                  <mat-label>{{ 'common.primary-insurance' | translate }}</mat-label>
                  <mat-select [value]="primaryInsurance$ | async" [compareWith]="compareById"
                    (selectionChange)="selectPrimaryInsurance($event.value)">
                    @for (insurance of insurances; track insurance.id) {
                      <mat-option [value]="insurance">
                        {{ insurance.clinicCarrierName }}
                        @if (insurance.memberId) {
                          ({{ insurance.memberId }})
                        }
                      </mat-option>
                    }
                  </mat-select>
                </mat-form-field>
              </td>
            </tr>
          }
        }
        <!-- Copay -->
        <tr class="mat-mdc-row">
          <td class="mat-mdc-cell input">
            {{ 'patient-details.estimate-card.table-two.copay' | translate }}
          </td>
          <td
            class="mat-mdc-cell editablefield text-center output"
            fxLayout="row"
            fxLayoutAlign="space-evenly center"
          >
            <div fxLayout="row" fxLayoutAlign="center center" fxLayoutGap="1rem" data-balance-benefit-cell
              [satPopoverAnchor]="copPrimary"
              (click)="copPrimary.open()"
              (popoverClosed)="valueChanged()">
              <span class="text-bold">Primary:</span>
              @if (isModified(PrimaryBenefitField.Copayment)) {
                <mat-icon
                  class="ambiguous-warning-icon"
                  color="warn"
                  [matTooltip]="'estimate.benefit-manually-modified' | translate"
                  matTooltipPosition="above"
                  (click)="$event.stopPropagation()"
                >warning</mat-icon>
              } @else if (isAmbiguous(PrimaryBenefitField.Copayment)) {
                <mat-icon
                  class="ambiguous-warning-icon"
                  color="warn"
                  [matTooltip]="'estimate.benefit-ambiguous' | translate"
                  matTooltipPosition="above"
                  (click)="$event.stopPropagation()"
                >warning</mat-icon>
              }
              @if (inputs.copayment != null) {
                <span class="editable">
                  {{ inputs.copayment | currency: 'USD' }}
                </span>
              } @else {
                <span class="editable">
                  {{ 'common.none' | translate }}
                </span>
              }
              <mat-icon class="edit-icon">edit</mat-icon>
              @if (isModified(PrimaryBenefitField.Copayment)) {
                <mat-icon
                  class="reset-icon"
                  [matTooltip]="'estimate.reset-to-eligibility' | translate"
                  matTooltipPosition="above"
                  (click)="resetToOriginal(PrimaryBenefitField.Copayment); $event.stopPropagation()"
                >refresh</mat-icon>
              }
              <sat-popover #copPrimary hasBackdrop>
                <mat-card>
                  <mat-card-content>
                    <mat-form-field appearance="outline">
                      <mat-label>{{ 'common.amount' | translate }}</mat-label>
                      <input
                        type="number"
                        matInput
                        [(ngModel)]="inputs.copayment"
                        (ngModelChange)="valueChanged()"
                        [ngModelOptions]="{ updateOn: 'blur' }"
                        (keyup)="onPopoverKeyup($event, copPrimary)"
                      />
                    </mat-form-field>
                  </mat-card-content>
                </mat-card>
              </sat-popover>
            </div>
            @if (hasSecondary) {
            <div fxLayout="row" fxLayoutAlign="center center" fxLayoutGap="1rem" data-balance-benefit-cell
              [satPopoverAnchor]="copSecondary"
              (click)="copSecondary.open()"
              (popoverClosed)="valueChanged()">
              <span class="text-bold">Secondary:</span>
              @if (isModified(SecondaryBenefitField.Copayment)) {
                <mat-icon
                  class="ambiguous-warning-icon"
                  color="warn"
                  [matTooltip]="'estimate.benefit-manually-modified' | translate"
                  matTooltipPosition="above"
                  (click)="$event.stopPropagation()"
                >warning</mat-icon>
              } @else if (isAmbiguous(SecondaryBenefitField.Copayment)) {
                <mat-icon
                  class="ambiguous-warning-icon"
                  color="warn"
                  [matTooltip]="'estimate.benefit-ambiguous' | translate"
                  matTooltipPosition="above"
                  (click)="$event.stopPropagation()"
                >warning</mat-icon>
              }
              @if (inputs.secondaryCopayment != null) {
                <span class="editable">
                  {{ inputs.secondaryCopayment | currency: 'USD' }}
                </span>
              } @else {
                <span class="editable">
                  {{ 'common.none' | translate }}
                </span>
              }
              <mat-icon class="edit-icon">edit</mat-icon>
              @if (isModified(SecondaryBenefitField.Copayment)) {
                <mat-icon
                  class="reset-icon"
                  [matTooltip]="'estimate.reset-to-eligibility' | translate"
                  matTooltipPosition="above"
                  (click)="resetToOriginal(SecondaryBenefitField.Copayment); $event.stopPropagation()"
                >refresh</mat-icon>
              }
              <sat-popover #copSecondary hasBackdrop>
                <mat-card>
                  <mat-card-content>
                    <mat-form-field appearance="outline">
                      <mat-label>{{ 'common.amount' | translate }}</mat-label>
                      <input
                        type="number"
                        matInput
                        [(ngModel)]="inputs.secondaryCopayment"
                        (ngModelChange)="valueChanged()"
                        [ngModelOptions]="{ updateOn: 'blur' }"
                        (keyup)="onPopoverKeyup($event, copSecondary)"
                      />
                    </mat-form-field>
                  </mat-card-content>
                </mat-card>
              </sat-popover>
            </div>
            }
          </td>
        </tr>
        <!-- Remaining Deductible -->
        <tr class="mat-mdc-row">
          <td class="mat-mdc-cell input">
            {{ 'patient-details.estimate-card.table-two.remaining-deductible' | translate }}
          </td>
          <td
            class="mat-mdc-cell editablefield text-center output"
            fxLayout="row"
            fxLayoutAlign="space-evenly center"
          >
            <div fxLayout="row" fxLayoutAlign="center center" fxLayoutGap="1rem" data-balance-benefit-cell
              [satPopoverAnchor]="dfrPrimary"
              (click)="dfrPrimary.open()"
              (popoverClosed)="valueChanged()">
              <span class="text-bold">Primary:</span>
              @if (isModified(PrimaryBenefitField.Deductible)) {
                <mat-icon
                  class="ambiguous-warning-icon"
                  color="warn"
                  [matTooltip]="'estimate.benefit-manually-modified' | translate"
                  matTooltipPosition="above"
                  (click)="$event.stopPropagation()"
                >warning</mat-icon>
              }
              @if (inputs.deductible != null) {
                <span class="editable">
                  {{ inputs.deductible | currency: 'USD' }}
                </span>
              } @else {
                <span class="editable">
                  {{ 'common.none' | translate }}
                </span>
              }
              <mat-icon class="edit-icon">edit</mat-icon>
              @if (isModified(PrimaryBenefitField.Deductible)) {
                <mat-icon
                  class="reset-icon"
                  [matTooltip]="'estimate.reset-to-eligibility' | translate"
                  matTooltipPosition="above"
                  (click)="resetToOriginal(PrimaryBenefitField.Deductible); $event.stopPropagation()"
                >refresh</mat-icon>
              }
              <sat-popover #dfrPrimary hasBackdrop>
                <mat-card>
                  <mat-card-content>
                    <mat-form-field appearance="outline">
                      <mat-label>{{ 'common.amount' | translate }}</mat-label>
                      <input
                        type="number"
                        matInput
                        [(ngModel)]="inputs.deductible"
                        (ngModelChange)="valueChanged()"
                        [ngModelOptions]="{ updateOn: 'blur' }"
                        (keyup)="onPopoverKeyup($event, dfrPrimary)"
                      />
                    </mat-form-field>
                  </mat-card-content>
                </mat-card>
              </sat-popover>
            </div>
            @if (hasSecondary) {
            <div fxLayout="row" fxLayoutAlign="center center" fxLayoutGap="1rem" data-balance-benefit-cell
              [satPopoverAnchor]="dfrSecondary"
              (click)="dfrSecondary.open()"
              (popoverClosed)="valueChanged()">
              <span class="text-bold">Secondary:</span>
              @if (isModified(SecondaryBenefitField.Deductible)) {
                <mat-icon
                  class="ambiguous-warning-icon"
                  color="warn"
                  [matTooltip]="'estimate.benefit-manually-modified' | translate"
                  matTooltipPosition="above"
                  (click)="$event.stopPropagation()"
                >warning</mat-icon>
              }
              @if (inputs.secondaryDeductible != null) {
                <span class="editable">
                  {{ inputs.secondaryDeductible | currency: 'USD' }}
                </span>
              } @else {
                <span class="editable">
                  {{ 'common.none' | translate }}
                </span>
              }
              <mat-icon class="edit-icon">edit</mat-icon>
              @if (isModified(SecondaryBenefitField.Deductible)) {
                <mat-icon
                  class="reset-icon"
                  [matTooltip]="'estimate.reset-to-eligibility' | translate"
                  matTooltipPosition="above"
                  (click)="resetToOriginal(SecondaryBenefitField.Deductible); $event.stopPropagation()"
                >refresh</mat-icon>
              }
              <sat-popover #dfrSecondary hasBackdrop>
                <mat-card>
                  <mat-card-content>
                    <mat-form-field appearance="outline">
                      <mat-label>{{ 'common.amount' | translate }}</mat-label>
                      <input
                        type="number"
                        matInput
                        [(ngModel)]="inputs.secondaryDeductible"
                        (ngModelChange)="valueChanged()"
                        [ngModelOptions]="{ updateOn: 'blur' }"
                        (keyup)="onPopoverKeyup($event, dfrSecondary)"
                      />
                    </mat-form-field>
                  </mat-card-content>
                </mat-card>
              </sat-popover>
            </div>
            }
          </td>
        </tr>
        <!-- Coinsurance -->
        <tr class="mat-mdc-row">
          <td class="mat-mdc-cell input">
            {{ 'patient-details.estimate-card.table-two.coinsurance' | translate }}
          </td>
          <td
            class="mat-mdc-cell editablefield text-center output"
            fxLayout="row"
            fxLayoutAlign="space-evenly center"
          >
            <div fxLayout="row" fxLayoutAlign="center center" fxLayoutGap="1rem" data-balance-benefit-cell
              [satPopoverAnchor]="coiPrimary"
              (click)="coiPrimary.open()"
              (popoverClosed)="valueChanged()">
              <span class="text-bold">Primary:</span>
              @if (isModified(PrimaryBenefitField.Coinsurance)) {
                <mat-icon
                  class="ambiguous-warning-icon"
                  color="warn"
                  [matTooltip]="'estimate.benefit-manually-modified' | translate"
                  matTooltipPosition="above"
                  (click)="$event.stopPropagation()"
                >warning</mat-icon>
              } @else if (isAmbiguous(PrimaryBenefitField.Coinsurance)) {
                <mat-icon
                  class="ambiguous-warning-icon"
                  color="warn"
                  [matTooltip]="'estimate.benefit-ambiguous' | translate"
                  matTooltipPosition="above"
                  (click)="$event.stopPropagation()"
                >warning</mat-icon>
              }
              @if (inputs.coinsurance != null) {
                <span class="editable">
                  {{ coinsurancePercent }}%
                </span>
              } @else {
                <span class="editable">
                  {{ 'common.none' | translate }}
                </span>
              }
              <mat-icon class="edit-icon">edit</mat-icon>
              @if (isModified(PrimaryBenefitField.Coinsurance)) {
                <mat-icon
                  class="reset-icon"
                  [matTooltip]="'estimate.reset-to-eligibility' | translate"
                  matTooltipPosition="above"
                  (click)="resetToOriginal(PrimaryBenefitField.Coinsurance); $event.stopPropagation()"
                >refresh</mat-icon>
              }
              <sat-popover #coiPrimary hasBackdrop>
                <mat-card>
                  <mat-card-content>
                    <mat-form-field appearance="outline">
                      <mat-label>{{ 'common.amount' | translate }}</mat-label>
                      <input
                        type="number"
                        min="0"
                        matInput
                        [(ngModel)]="coinsurancePercent"
                        (ngModelChange)="valueChanged()"
                        [ngModelOptions]="{ updateOn: 'blur' }"
                        (keyup)="onPopoverKeyup($event, coiPrimary)"
                      />
                    </mat-form-field>
                  </mat-card-content>
                </mat-card>
              </sat-popover>
            </div>
            @if (hasSecondary) {
            <div fxLayout="row" fxLayoutAlign="center center" fxLayoutGap="1rem" data-balance-benefit-cell
              [satPopoverAnchor]="coiSecondary"
              (click)="coiSecondary.open()"
              (popoverClosed)="valueChanged()">
              <span class="text-bold">Secondary:</span>
              @if (isModified(SecondaryBenefitField.Coinsurance)) {
                <mat-icon
                  class="ambiguous-warning-icon"
                  color="warn"
                  [matTooltip]="'estimate.benefit-manually-modified' | translate"
                  matTooltipPosition="above"
                  (click)="$event.stopPropagation()"
                >warning</mat-icon>
              } @else if (isAmbiguous(SecondaryBenefitField.Coinsurance)) {
                <mat-icon
                  class="ambiguous-warning-icon"
                  color="warn"
                  [matTooltip]="'estimate.benefit-ambiguous' | translate"
                  matTooltipPosition="above"
                  (click)="$event.stopPropagation()"
                >warning</mat-icon>
              }
              @if (inputs.secondaryCoinsurance != null) {
                <span class="editable">
                  {{ secondaryCoinsurancePercent }}%
                </span>
              } @else {
                <span class="editable">
                  {{ 'common.none' | translate }}
                </span>
              }
              <mat-icon class="edit-icon">edit</mat-icon>
              @if (isModified(SecondaryBenefitField.Coinsurance)) {
                <mat-icon
                  class="reset-icon"
                  [matTooltip]="'estimate.reset-to-eligibility' | translate"
                  matTooltipPosition="above"
                  (click)="resetToOriginal(SecondaryBenefitField.Coinsurance); $event.stopPropagation()"
                >refresh</mat-icon>
              }
              <sat-popover #coiSecondary hasBackdrop>
                <mat-card>
                  <mat-card-content>
                    <mat-form-field appearance="outline">
                      <mat-label>{{ 'common.amount' | translate }}</mat-label>
                      <input
                        type="number"
                        min="0"
                        matInput
                        [(ngModel)]="secondaryCoinsurancePercent"
                        (ngModelChange)="valueChanged()"
                        [ngModelOptions]="{ updateOn: 'blur' }"
                        (keyup)="onPopoverKeyup($event, coiSecondary)"
                      />
                    </mat-form-field>
                  </mat-card-content>
                </mat-card>
              </sat-popover>
            </div>
            }
          </td>
        </tr>

        <!-- Out-of-Pocket Max -->
        <tr class="mat-mdc-row">
          <td class="mat-mdc-cell input">
            {{ 'patient-details.estimate-card.table-two.out-of-pocket' | translate }}
          </td>
          <td
            class="mat-mdc-cell editablefield text-center output"
            fxLayout="row"
            fxLayoutAlign="space-evenly center"
          >
            <div
              fxLayout="row"
              fxLayoutAlign="center center"
              fxLayoutGap="1rem"
              data-balance-benefit-cell
              [satPopoverAnchor]="outOfPocketMaxPopover"
              (click)="outOfPocketMaxPopover.open()"
              (popoverClosed)="valueChanged()"
            >
              <span class="text-bold">Primary:</span>

              @if (isModified(PrimaryBenefitField.OutOfPocketMax)) {
                <mat-icon
                  class="ambiguous-warning-icon"
                  color="warn"
                  [matTooltip]="'estimate.benefit-manually-modified' | translate"
                  matTooltipPosition="above"
                  (click)="$event.stopPropagation()"
                >warning</mat-icon>
              }
              @if (inputs.outOfPocketMax != null) {
                <span class="editable">
                  {{ inputs.outOfPocketMax | currency: 'USD' }}
                </span>
              } @else {
                <span class="editable">
                  {{ 'common.none' | translate }}
                </span>
              }
              <mat-icon class="edit-icon">edit</mat-icon>
              @if (isModified(PrimaryBenefitField.OutOfPocketMax)) {
                <mat-icon
                  class="reset-icon"
                  [matTooltip]="'estimate.reset-to-eligibility' | translate"
                  matTooltipPosition="above"
                  (click)="resetToOriginal(PrimaryBenefitField.OutOfPocketMax); $event.stopPropagation()"
                >refresh</mat-icon>
              }

              <sat-popover #outOfPocketMaxPopover hasBackdrop>
                <mat-card>
                  <mat-card-content>
                    <mat-form-field appearance="outline">
                      <mat-label>{{ 'common.amount' | translate }}</mat-label>
                      <input
                        type="number"
                        matInput
                        [(ngModel)]="inputs.outOfPocketMax"
                        (ngModelChange)="valueChanged()"
                        [ngModelOptions]="{ updateOn: 'blur' }"
                        [disabled]="inputs.outOfPocketMax == null"
                        (keyup)="onPopoverKeyup($event, outOfPocketMaxPopover)"
                      />
                    </mat-form-field>
                    <mat-checkbox
                      [checked]="inputs.outOfPocketMax == null"
                      (change)="onOutOutPocketNilChange($event)"
                    >
                      {{ 'common.none' | translate }}
                    </mat-checkbox>
                  </mat-card-content>
                </mat-card>
              </sat-popover>
            </div>

            @if (hasSecondary) {
              <div
                fxLayout="row"
                fxLayoutAlign="center center"
                fxLayoutGap="1rem"
                data-balance-benefit-cell
                [satPopoverAnchor]="outOfPocketMaxSecondaryPopover"
                (click)="outOfPocketMaxSecondaryPopover.open()"
                (popoverClosed)="valueChanged()"
              >
                <span class="text-bold">Secondary:</span>

                @if (isModified(SecondaryBenefitField.OutOfPocketMax)) {
                  <mat-icon
                    class="ambiguous-warning-icon"
                    color="warn"
                    [matTooltip]="'estimate.benefit-manually-modified' | translate"
                    matTooltipPosition="above"
                    (click)="$event.stopPropagation()"
                  >warning</mat-icon>
                }
                @if (inputs.secondaryOutOfPocketMax != null) {
                  <span class="editable">
                    {{ inputs.secondaryOutOfPocketMax | currency: 'USD' }}
                  </span>
                } @else {
                  <span class="editable">
                    {{ 'common.none' | translate }}
                  </span>
                }
                <mat-icon class="edit-icon">edit</mat-icon>
                @if (isModified(SecondaryBenefitField.OutOfPocketMax)) {
                  <mat-icon
                    class="reset-icon"
                    [matTooltip]="'estimate.reset-to-eligibility' | translate"
                    matTooltipPosition="above"
                    (click)="resetToOriginal(SecondaryBenefitField.OutOfPocketMax); $event.stopPropagation()"
                  >refresh</mat-icon>
                }

                <sat-popover #outOfPocketMaxSecondaryPopover hasBackdrop>
                  <mat-card>
                    <mat-card-content>
                      <mat-form-field appearance="outline">
                        <mat-label>{{ 'common.amount' | translate }}</mat-label>
                        <input
                          type="number"
                          matInput
                          [(ngModel)]="inputs.secondaryOutOfPocketMax"
                          (ngModelChange)="valueChanged()"
                          [ngModelOptions]="{ updateOn: 'blur' }"
                          [disabled]="inputs.secondaryOutOfPocketMax == null"
                          (keyup)="onPopoverKeyup($event, outOfPocketMaxSecondaryPopover)"
                        />
                      </mat-form-field>
                      <mat-checkbox
                        [checked]="inputs.secondaryOutOfPocketMax == null"
                        (change)="onSecondaryOutOfPocketNilChange($event)"
                      >
                        {{ 'common.none' | translate }}
                      </mat-checkbox>
                    </mat-card-content>
                  </mat-card>
                </sat-popover>
              </div>
            }
          </td>
        </tr>
        @if (secondaryInsurances$ | async; as insurances) {
          @if (insurances?.length > 1) {
            <tr class="mat-mdc-row">
              <td class="mat-mdc-cell input">
                {{ 'common.secondary-insurance' | translate }}
              </td>
              <td class="mat-mdc-cell editablefield text-center output">
                <mat-form-field class="w-100" subscriptSizing="dynamic">
                  <mat-label>{{ 'common.secondary-insurance' | translate }}</mat-label>
                  <mat-select [value]="secondaryInsurance$ | async" [compareWith]="compareById"
                    (selectionChange)="selectSecondaryInsurance($event.value)">
                    @for (insurance of insurances; track insurance.id) {
                      <mat-option [value]="insurance">
                        {{ insurance.clinicCarrierName }}
                        @if (insurance.memberId) {
                          ({{ insurance.memberId }})
                        }
                      </mat-option>
                    }
                  </mat-select>
                </mat-form-field>
              </td>
            </tr>
          }
        }
      </table>
    </div>
  </mat-card-content>
</mat-card>

<sat-popover #secondaryInsuranceOptions hasBackdrop verticalAlign="below">
  <mat-card>
    <mat-card-content fxLayout="column" fxLayoutGap="1rem">
      @if ((hasSecondaryInsurance) === false) {
        <div>
          {{'patient-details.estimate-card.advanced.no-secondary-insurance-on-file' | translate}}
        </div>
      } @else {
        <mat-slide-toggle fxFlex [(ngModel)]="inputs.hasSecondary"
          (ngModelChange)="valueChanged()">
          {{ 'patient-details.estimate-card.advanced.secondary-insurance' | translate }}
        </mat-slide-toggle>
        @if (inputs.hasSecondary) {
          <mat-slide-toggle fxFlex [(ngModel)]="inputs.secondaryInsuranceCoversDeductible"
            (ngModelChange)="valueChanged()">
            {{ 'patient-details.estimate-card.advanced.secondary-covers-deductible' | translate }}
          </mat-slide-toggle>
          <mat-slide-toggle fxFlex [(ngModel)]="inputs.secondaryInsuranceCoversCopayment"
            (ngModelChange)="valueChanged()">
            {{ 'patient-details.estimate-card.advanced.secondary-covers-copayment' | translate }}
          </mat-slide-toggle>
          <mat-slide-toggle fxFlex [(ngModel)]="inputs.secondaryInsuranceCoversCoinsurance"
            (ngModelChange)="valueChanged()">
            {{ 'patient-details.estimate-card.advanced.secondary-covers-coinsurance' | translate }}
          </mat-slide-toggle>
        }
      }
    </mat-card-content>
  </mat-card>
</sat-popover>
